import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import FilterBar from '../components/ui/FilterBar';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import { useEntities } from '../hooks/useEntities';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/goods-receipts`;
const PAGE_SIZE = 50;

interface GR { id: number; gr_number: string; gr_date: string; po_number: string; po_line_number: number; vendor_id: string; material_code: string; quantity: number; amount: number; lifecycle_status: string; entity_code: string; je_id: number; posted_date: string; version: number; created_at: string; }
interface Summary { total: number; received: number; posted: number; reversed: number; }
interface POLine { line_number: number; material_code: string; description: string; quantity: number; unit_price: number; line_amount: number; gl_account: string; received_quantity: number; }
interface PO { id: number; po_number: string; vendor_id: string; vendor_name: string; total_amount: number; entity_code: string; lifecycle_status: string; lines: POLine[]; }

export default function GoodsReceipts() {
  const entities = useEntities();
  const { entity } = useGlobalEntity();
  const [grs, setGRs] = useState<GR[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [poFilter, setPoFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  // Modal-local error banner. Toasts at z-index 2000 are hidden by
  // modal overlays at higher z-index, so errors raised by an action
  // inside the Receive Goods modal need to render INSIDE the modal
  // too. Persists until user dismisses or retries. Same pattern as
  // APInvoices upload modal (2026-05-19 z-index race incident).
  const [modalError, setModalError] = useState<string | null>(null);

  // Smart GR creation state
  const [issuedPOs, setIssuedPOs] = useState<PO[]>([]);
  const [selectedPO, setSelectedPO] = useState<PO | null>(null);
  const [receiveLines, setReceiveLines] = useState<{ line_number: number; receive: boolean; qty: string; }[]>([]);
  const [grNumber, setGrNumber] = useState('');
  const [receiver, setReceiver] = useState('');
  const [creating, setCreating] = useState(false);

  // Errors persist until X clicked; success auto-dismisses in 4s.
  // Per Albert's 2026-05-19 feedback: actionable failures vanishing
  // in 4 seconds are missed by users still reading the message.
  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    if (ok) setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (statusFilter) p.set('lifecycle_status', statusFilter);
    if (poFilter) p.set('po_number', poFilter);
    try {
      const [listRes, sumRes] = await Promise.all([
        authFetch(withEntityParam(`${API}?${p}`, entity)),
        authFetch(withEntityParam(`${API}/summary`, entity)),
      ]);
      if (listRes.ok) { const d = await listRes.json(); setGRs(d.items || []); setTotal(d.total || 0); }
      if (sumRes.ok) { setSummary(await sumRes.json()); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, poFilter, entity]);

  // Load open POs on mount
  const [openPOs, setOpenPOs] = useState<PO[]>([]);
  const loadOpenPOs = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        authFetch(withEntityParam(`${API_V1}/purchase-orders?lifecycle_status=ISSUED&limit=100`, entity)),
        authFetch(withEntityParam(`${API_V1}/purchase-orders?lifecycle_status=PARTIALLY_RECEIVED&limit=100`, entity)),
      ]);
      const d1 = r1.ok ? (await r1.json()).items || [] : [];
      const d2 = r2.ok ? (await r2.json()).items || [] : [];
      setOpenPOs([...d1, ...d2]);
    } catch { }
  }, [entity]);

  // Get current user name for auto-populating receiver
  const currentUserName = (() => {
    try { const u = JSON.parse(localStorage.getItem('agentcfo_user') || 'null'); return u?.full_name || ''; } catch { return ''; }
  })();

  useEffect(() => { load(); loadOpenPOs(); }, [load, loadOpenPOs]);

  // Load issued POs when modal opens
  const openCreate = async () => {
    setShowCreate(true);
    setSelectedPO(null);
    setReceiveLines([]);
    setGrNumber('');
    setReceiver(currentUserName);
    setModalError(null);
    try {
      const res = await authFetch(withEntityParam(`${API_V1}/purchase-orders?lifecycle_status=ISSUED&limit=100`, entity));
      if (res.ok) {
        const d = await res.json();
        setIssuedPOs(d.items || []);
      }
      // Also try PARTIALLY_RECEIVED and APPROVED
      const res2 = await authFetch(withEntityParam(`${API_V1}/purchase-orders?lifecycle_status=PARTIALLY_RECEIVED&limit=100`, entity));
      if (res2.ok) {
        const d2 = await res2.json();
        setIssuedPOs(prev => [...prev, ...(d2.items || [])]);
      }
    } catch { }
  };

  // When PO selected, load its lines and set up receive checkboxes
  const selectPO = async (poId: string) => {
    if (!poId) { setSelectedPO(null); setReceiveLines([]); return; }
    try {
      const res = await authFetch(`${API_V1}/purchase-orders/${poId}`);
      if (res.ok) {
        const po = await res.json();
        setSelectedPO(po);
        // Set up receive lines — default checked for lines with remaining qty
        setReceiveLines((po.lines || []).map((l: POLine) => ({
          line_number: l.line_number,
          receive: (l.quantity - (l.received_quantity || 0)) > 0,
          qty: String(l.quantity - (l.received_quantity || 0)),
        })));
      }
    } catch { }
  };

  // Create GRs for each selected line
  const handleReceive = async () => {
    if (!selectedPO) return;
    setCreating(true);
    setModalError(null);
    let created = 0;
    let failed = 0;
    const failures: string[] = [];

    for (const rl of receiveLines) {
      if (!rl.receive || !parseFloat(rl.qty)) continue;
      const poLine = selectedPO.lines?.find(l => l.line_number === rl.line_number);
      if (!poLine) continue;

      try {
        const payload = {
          gr_number: grNumber ? `${grNumber}-L${rl.line_number}` : '',
          po_number: selectedPO.po_number,
          po_line_number: rl.line_number,
          material_code: poLine.material_code || '',
          quantity: parseFloat(rl.qty),
          amount: parseFloat(rl.qty) * (poLine.unit_price || 0),
          entity_code: selectedPO.entity_code || '',
          receiver: receiver,
        };
        const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (res.ok) {
          created++;
          // Auto-post the GR
          const gr = await res.json();
          if (gr.id) {
            const postRes = await authFetch(`${API}/${gr.id}/post`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expected_version: gr.version || 1 }) });
            if (!postRes.ok) {
              const postErr = await postRes.json().catch(() => ({}));
              failures.push(`Line ${rl.line_number} post: ${postErr.detail || `HTTP ${postRes.status}`}`);
              console.error(`GR ${gr.id} post failed:`, postErr.detail || postRes.status);
            }
          }
        } else {
          failed++;
          const err = await res.json().catch(() => ({}));
          failures.push(`Line ${rl.line_number}: ${err.detail || `HTTP ${res.status}`}`);
        }
      } catch (err: any) {
        failed++;
        failures.push(`Line ${rl.line_number}: ${err?.message || 'Network error'}`);
      }
    }

    setCreating(false);
    if (created > 0 && failed === 0 && failures.length === 0) {
      showToast(`${created} GR(s) created & posted`, true);
      setShowCreate(false);
      load();
      loadOpenPOs();
    } else if (created > 0) {
      // Partial success: keep modal open so user sees which lines failed.
      const msg = `${created} GR(s) created, ${failures.length} failed:\n${failures.join('\n')}`;
      setModalError(msg);
      showToast(`${created} created, ${failures.length} failed`, false);
      load();
      loadOpenPOs();
    } else {
      const msg = failures.length > 0
        ? `No items received:\n${failures.join('\n')}`
        : 'No items received — check at least one line and enter a receive quantity.';
      setModalError(msg);
      showToast('Receive failed', false);
    }
  };

  const handlePost = async (id: number, version: number) => {
    try {
      const res = await authFetch(`${API}/${id}/post`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expected_version: version }) });
      if (res.ok) { showToast('GR posted with GRNI JE', true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Post failed', false); }
  };

  const quickReceive = async (po: PO) => {
    setShowCreate(true);
    setIssuedPOs(openPOs);
    setGrNumber('');
    setReceiver(currentUserName);
    await selectPO(String(po.id));
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const columns: Column[] = [
    { key: 'gr_number', label: 'GR #', render: (v: any) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'gr_date', label: 'Date', render: (v: any) => <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{v}</span> },
    { key: 'po_number', label: 'PO #' },
    { key: 'material_code', label: 'Material', render: (v: any) => <span style={{ color: 'var(--text-secondary)' }}>{v || '-'}</span> },
    { key: 'quantity', label: 'Qty' },
    { key: 'amount', label: 'Amount', align: 'right', render: (v: any) => <span style={{ fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'lifecycle_status', label: 'Status', render: (_: any, row: GR) => <StatusBadge status={row.lifecycle_status} /> },
    { key: 'je_id', label: 'JE', render: (v: any) => <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{v ? `#${v}` : '-'}</span> },
    {
      key: 'actions', label: 'Actions', render: (_: any, row: GR) => (
        row.lifecycle_status === 'RECEIVED'
          ? <ActionButton label="Post" onClick={(e) => { e.stopPropagation(); handlePost(row.id, row.version); }} variant="success" size="sm" />
          : null
      ),
    },
  ];

  return (
    <PageLayout title="Goods Receipts" subtitle="Receive goods against purchase orders and post GRNI journal entries" action={{ label: '+ Receive Goods', onClick: openCreate }}>
      {summary && (
        <KpiGrid items={[
          { label: 'Total', value: summary.total, color: 'var(--text-primary)' },
          { label: 'Received', value: summary.received, color: 'var(--warning)' },
          { label: 'Posted', value: summary.posted, color: 'var(--positive)' },
          { label: 'Reversed', value: summary.reversed, color: 'var(--negative)' },
        ]} />
      )}

      {/* Open POs Awaiting Receipt */}
      {openPOs.length > 0 && (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: 'var(--warning)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Open POs Awaiting Receipt ({openPOs.length})
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ borderBottom: '2px solid var(--border-subtle)' }}>
              {['PO #', 'Vendor', 'Entity', 'Amount', 'Status', ''].map(h => (
                <th key={h} style={{ textAlign: h === 'Amount' ? 'right' : 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' as const }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {openPOs.map(po => (
                <tr key={po.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--accent)' }}>{po.po_number}</td>
                  <td style={{ padding: '8px 10px' }}>{po.vendor_name || po.vendor_id}</td>
                  <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{po.entity_code}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{formatAmount(po.total_amount)}</td>
                  <td style={{ padding: '8px 10px' }}><StatusBadge status={po.lifecycle_status} /></td>
                  <td style={{ padding: '8px 10px' }}><ActionButton label="Receive" onClick={() => quickReceive(po)} variant="success" size="sm" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FilterBar filters={[
        { key: 'po', label: 'PO #', type: 'search', value: poFilter, onChange: v => { setPoFilter(v); setPage(0); }, width: 200, placeholder: 'Filter by PO #' },
        { key: 'status', label: 'Statuses', type: 'select', value: statusFilter, onChange: v => { setStatusFilter(v); setPage(0); }, options: ['RECEIVED', 'POSTED', 'REVERSED'].map(s => ({ value: s, label: s })), width: 180 },
      ]} />

      <DataTable columns={columns} rows={grs} loading={loading} emptyMessage="No goods receipts found." rowKey="id" />

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16, alignItems: 'center' }}>
          <ActionButton label="PREV" onClick={() => setPage(Math.max(0, page - 1))} variant="secondary" size="sm" disabled={page === 0} />
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
          <ActionButton label="NEXT" onClick={() => setPage(page + 1)} variant="secondary" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} />
        </div>
      )}

      {/* Smart GR Modal — PO-driven with partial reception */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Receive Goods" size="lg" footer={
        <>
          <ActionButton label="Cancel" onClick={() => setShowCreate(false)} variant="secondary" />
          <ActionButton label={creating ? 'Receiving...' : 'Receive & Post'} onClick={handleReceive} variant="success" disabled={creating || !selectedPO} />
        </>
      }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Inline error banner -- always visible inside the modal so it
              isn't hidden behind the modal overlay like the outside-modal
              toast (z-index race). Persists until user dismisses or retries. */}
          {modalError && (
            <div style={{
              padding: '12px 16px', borderRadius: 8,
              background: 'var(--negative-bg)', color: 'var(--negative)',
              border: '1px solid var(--negative)',
              fontSize: 13, lineHeight: 1.5,
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <div style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Receive failed</div>
                {modalError}
              </div>
              <button
                type="button"
                onClick={() => setModalError(null)}
                aria-label="Dismiss error"
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'inherit', fontSize: 18, fontWeight: 700,
                  lineHeight: 1, padding: 0, marginTop: -2,
                }}
              >×</button>
            </div>
          )}
          {/* Step 1: Select PO */}
          <select value={selectedPO?.id || ''} onChange={e => selectPO(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">1. Select Purchase Order *</option>
            {issuedPOs.map(po => (
              <option key={po.id} value={po.id}>
                {po.po_number} — {po.vendor_name || po.vendor_id} ({formatAmount(po.total_amount)}) [{po.entity_code}]
              </option>
            ))}
          </select>
          {issuedPOs.length === 0 && <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic' }}>No issued POs available for receiving</div>}

          {/* Step 2: GR details */}
          {selectedPO && (
            <div style={{ display: 'flex', gap: 8 }}>
              <input placeholder="GR Number (auto if blank)" value={grNumber} onChange={e => setGrNumber(e.target.value)} style={inputStyle} />
              <input placeholder="Receiver" value={receiver} readOnly style={{ ...inputStyle, background: 'var(--accent-subtle, #eef2ff)', fontWeight: 600 }} title="Auto-set to logged-in user" />
            </div>
          )}

          {/* Step 3: PO lines with receive checkboxes */}
          {selectedPO && selectedPO.lines && (
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, color: 'var(--positive)' }}>
                PO LINES — Select items to receive (partial reception supported)
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead><tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  {['', '#', 'Material', 'Description', 'Ordered', 'Received', 'Remaining', 'Receive Qty', 'GL'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '4px 6px', fontSize: 9, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {selectedPO.lines.map((l, i) => {
                    const remaining = l.quantity - (l.received_quantity || 0);
                    const rl = receiveLines[i];
                    if (!rl) return null;
                    return (
                      <tr key={l.line_number} style={{ borderBottom: '1px solid var(--border-subtle)', opacity: remaining <= 0 ? 0.4 : 1 }}>
                        <td style={{ padding: '6px' }}>
                          <input type="checkbox" checked={rl.receive && remaining > 0} disabled={remaining <= 0}
                            onChange={e => { const lines = [...receiveLines]; lines[i].receive = e.target.checked; setReceiveLines(lines); }} />
                        </td>
                        <td style={{ padding: '6px' }}>{l.line_number}</td>
                        <td style={{ padding: '6px', fontWeight: 600 }}>{l.material_code || '-'}</td>
                        <td style={{ padding: '6px' }}>{l.description}</td>
                        <td style={{ padding: '6px' }}>{l.quantity}</td>
                        <td style={{ padding: '6px', color: 'var(--positive)' }}>{l.received_quantity || 0}</td>
                        <td style={{ padding: '6px', fontWeight: 600, color: remaining > 0 ? 'var(--warning)' : 'var(--positive)' }}>{remaining}</td>
                        <td style={{ padding: '6px' }}>
                          <input type="number" value={rl.qty} disabled={remaining <= 0}
                            onChange={e => { const lines = [...receiveLines]; lines[i].qty = e.target.value; setReceiveLines(lines); }}
                            style={{ ...inputStyle, width: 70, padding: '4px 6px' }} max={remaining} min={0} />
                        </td>
                        <td style={{ padding: '6px', color: 'var(--text-secondary)', fontSize: 10 }}>{l.gl_account || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-secondary)' }}>
                Receiving {receiveLines.filter(r => r.receive && parseFloat(r.qty) > 0).length} of {selectedPO.lines.length} lines
                {' — '}Total: {formatAmount(receiveLines.reduce((sum, rl, i) => {
                  if (!rl.receive || !parseFloat(rl.qty)) return sum;
                  const line = selectedPO.lines[i];
                  return sum + (parseFloat(rl.qty) * (line?.unit_price || 0));
                }, 0))}
              </div>
              <div style={{ marginTop: 4, fontSize: 10, color: 'var(--accent)' }}>
                On post: DR Inventory/Expense (GL from PO line) → CR GRNI Accrual → Auto-triggers 3-way match
              </div>
            </div>
          )}
        </div>
      </Modal>

      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 2000,
          padding: '12px 16px 12px 20px', borderRadius: 8,
          background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)',
          color: toast.ok ? 'var(--positive)' : 'var(--negative)',
          fontSize: 13, fontWeight: 600,
          maxWidth: 520, lineHeight: 1.4,
          display: 'flex', alignItems: 'flex-start', gap: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }}>
          <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{toast.msg}</span>
          {!toast.ok && (
            <button
              type="button"
              onClick={() => setToast(null)}
              aria-label="Dismiss"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'inherit', fontSize: 18, fontWeight: 700,
                lineHeight: 1, padding: 0, marginTop: -2,
              }}
            >×</button>
          )}
        </div>
      )}
    </PageLayout>
  );
}
